/**
 * Normalize Prep review into a display-only breakdown (ADR-003 only).
 * Does not invent totals or commit pricing — surfaces fields the Prep APIs already return.
 */

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function finiteNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {string|null|undefined} size
 * @returns {string|null}
 */
function formatTruckLabel(size) {
  const raw = String(size || "").trim();
  return raw ? raw.replace(/_/g, " ") : null;
}

/**
 * @param {string|null|undefined} corridor
 * @returns {string|null}
 */
function formatCorridorValue(corridor) {
  if (corridor == null) return null;
  if (typeof corridor === "string") {
    const s = corridor.trim();
    return s || null;
  }
  if (typeof corridor === "object") {
    const origin = corridor.originRegion || corridor.origin || corridor.from;
    const dest =
      corridor.destinationRegion || corridor.destination || corridor.to;
    if (origin && dest) return `${origin} → ${dest}`;
    if (corridor.label) return String(corridor.label);
    if (corridor.code) return String(corridor.code);
  }
  return null;
}

/**
 * @param {{ key?: string, ruleKey?: string, description?: string }} line
 * @returns {"transport"|"extras"|"other"}
 */
export function classifySuggestionLine(line) {
  const key = String(line?.ruleKey || line?.key || "").toLowerCase();
  const desc = String(line?.description || "").toLowerCase();
  if (
    key.includes("transport") ||
    key === "base" ||
    key.startsWith("corridor") ||
    key.includes("baseamount") ||
    key.startsWith("local.volume") ||
    key.startsWith("local.mileage") ||
    key.startsWith("shared.linehaul") ||
    key.startsWith("shared.collection") ||
    key.startsWith("shared.delivery") ||
    /^transport\b/.test(desc)
  ) {
    return "transport";
  }
  if (
    key.startsWith("access.") ||
    key.startsWith("access") ||
    key.startsWith("packing") ||
    key.startsWith("extras.") ||
    key.includes("addon") ||
    key.includes("adjustment") ||
    key.includes("rounding") ||
    /\b(access|packing|extra|addon|adjustment)\b/.test(desc)
  ) {
    return "extras";
  }
  return "other";
}

/**
 * @param {object|null|undefined} review
 * @returns {{
 *   source: "prep",
 *   total: number|null,
 *   context: { key: string, label: string, value: string }[],
 *   lines: { key: string, label: string, amount: number|null, group: string }[],
 *   transportTotal: number|null,
 *   extrasTotal: number|null,
 *   isPreview: false,
 * }|null}
 */
export function mapPrepReviewToBreakdown(review) {
  if (!review || typeof review !== "object") return null;

  const explanation =
    review.pricingExplanation && typeof review.pricingExplanation === "object"
      ? review.pricingExplanation
      : {};

  const total =
    finiteNumber(review.suggestedTotal) ??
    finiteNumber(explanation.finalTotal) ??
    finiteNumber(review.totalCost);

  /** @type {{ key: string, label: string, value: string }[]} */
  const context = [];
  const truck =
    formatTruckLabel(explanation.selectedTruckSize) ||
    formatTruckLabel(explanation.recommendedTruckSize);
  if (truck) context.push({ key: "truck", label: "Truck", value: truck });

  const volume =
    finiteNumber(review.billableVolumeM3) ??
    finiteNumber(explanation.billableVolumeM3);
  if (volume != null) {
    context.push({
      key: "volume",
      label: "Billable volume",
      value: `${volume.toFixed(2)} m³`,
    });
  }

  const rate =
    finiteNumber(explanation.ratePerM3) ??
    finiteNumber(
      explanation.corridor && typeof explanation.corridor === "object"
        ? explanation.corridor.ratePerM3
        : null
    );
  if (rate != null) {
    context.push({
      key: "rate",
      label: "Corridor rate",
      value: `R ${rate.toLocaleString("en-ZA")}/m³`,
    });
  }

  const corridorLabel =
    formatCorridorValue(explanation.corridor) ||
    (review.corridorGuidance?.status === "ok" ||
    review.corridorGuidance?.status === "found"
      ? formatCorridorValue(review.corridorGuidance)
      : null);
  if (corridorLabel) {
    context.push({ key: "corridor", label: "Corridor", value: corridorLabel });
  }

  const routeKm =
    finiteNumber(review.route?.routeKm) ?? finiteNumber(explanation.routeKm);
  if (routeKm != null) {
    context.push({
      key: "distance",
      label: "Route distance",
      value: `${routeKm} km`,
    });
  }

  const rawLines = Array.isArray(review.pricingLines) ? review.pricingLines : [];
  /** @type {{ key: string, label: string, amount: number|null, group: string }[]} */
  let lines = rawLines
    .filter((line) => line && (line.description || line.amount != null))
    .map((line, index) => ({
      key: String(line.key || line.ruleKey || `line-${index + 1}`),
      label: String(line.description || line.ruleKey || `Line ${index + 1}`),
      amount: finiteNumber(line.amount),
      group: classifySuggestionLine(line),
    }));

  // When sell lines are missing, surface explanation money fields as summary rows.
  if (lines.length === 0) {
    const transportAmount = finiteNumber(explanation.transportAmount);
    if (transportAmount != null) {
      lines.push({
        key: "transport",
        label: "Transport",
        amount: transportAmount,
        group: "transport",
      });
    }
    const packing = finiteNumber(explanation.packingCharge);
    if (packing != null && packing !== 0) {
      lines.push({
        key: "packing",
        label: "Packing",
        amount: packing,
        group: "extras",
      });
    }
    const accessList = Array.isArray(explanation.accessCharges)
      ? explanation.accessCharges
      : [];
    accessList.forEach((charge, index) => {
      const amount = finiteNumber(charge?.amount);
      if (amount == null) return;
      lines.push({
        key: charge?.ruleKey || `access-${index + 1}`,
        label: String(charge?.description || charge?.ruleKey || "Access"),
        amount,
        group: "extras",
      });
    });
  }

  const sumGroup = (group) => {
    const amounts = lines
      .filter((l) => l.group === group)
      .map((l) => l.amount)
      .filter((n) => n != null);
    if (amounts.length === 0) return null;
    return amounts.reduce((sum, n) => sum + n, 0);
  };

  if (total == null && lines.length === 0 && context.length === 0) {
    return null;
  }

  return {
    source: "prep",
    total,
    context,
    lines,
    notes: [],
    transportTotal: sumGroup("transport"),
    extrasTotal: sumGroup("extras"),
    linesSumToTotal: null,
    isPreview: false,
  };
}

/**
 * ADR-003 Prep review only — no legacy lead pricing-suggestion path.
 * @param {{ review?: object|null }} input
 */
export function mapEngineSuggestionBreakdown({ review = null } = {}) {
  return mapPrepReviewToBreakdown(review);
}
